// Code taken from: platform/packages/ai-mate/conversation-assistant-utils/src/common/hooks/usePostOfficePlacement.ts
import { useEffect, useState } from 'react';

// eslint-disable-next-line @atlassian/post-office-headless-api/no-placement-remote -- Post Office RML placement not recommended in platform. Use headless API instead (@atlassian/post-office-headless-api).
import type { PlacementProps } from '@atlassian/post-office-placement-remote/placement';

type UsePostOfficePlacementResult = {
	PostOfficePlacement: React.ComponentType<PlacementProps> | null;
	isMounted: boolean;
};

/**
 * This hook dynamically imports the Placement component from the @atlassian/post-office-placement-remote package.
 * The dynamic import is used to avoid SSR altogether, as the Placement was clashing with SSR.
 * Supports test mocking via window.__mockPostOfficePlacement__
 */

export const usePostOfficePlacement = (): UsePostOfficePlacementResult => {
	const [PostOfficePlacement, setPostOfficePlacement] =
		useState<React.ComponentType<PlacementProps> | null>(null);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			let isComponentMounted = true;

			// Check for mock in test environments (set by VR test)
			const mockModule = (window as any).__mockPostOfficePlacement__;
			if (mockModule) {
				setPostOfficePlacement(() => mockModule);
				setIsMounted(true);
				return;
			}

			// eslint-disable-next-line @atlassian/post-office-headless-api/no-placement-remote -- Post Office RML placement not recommended in platform. Disabled lint rule for existing usage.
			import('@atlassian/post-office-placement-remote')
				.then((module) => {
					if (isComponentMounted) {
						setPostOfficePlacement(() => module.Placement);
						setIsMounted(true);
					}
				})
				.catch(() => {
					// Silently fail if import fails - PostOfficePlacement will remain null
					// This prevents the component from being stuck in a loading state
					if (isComponentMounted) {
						setIsMounted(true);
					}
				});

			return () => {
				isComponentMounted = false;
			};
		}
	}, []);

	return { PostOfficePlacement, isMounted };
};
